// Calendar (react-day-picker v8) — unified UI aligned with design system + blue accent

import {
  DayPicker,
  type DayPickerRangeProps,
  type DayPickerSingleProps,
  type Matcher,
} from 'react-day-picker'

import { cn, focusRing } from '@/lib/utils'
import RiArrowLeftSLine from '~icons/ri/arrow-left-s-line.jsx'
import RiArrowRightSLine from '~icons/ri/arrow-right-s-line.jsx'

type OmitKeys<T, K extends keyof T> = {
  [P in keyof T as P extends K ? never : P]: T[P]
}

type KeysToOmit = 'showWeekNumber' | 'mode'

type SingleProps = OmitKeys<DayPickerSingleProps, KeysToOmit>
type RangeProps = OmitKeys<DayPickerRangeProps, KeysToOmit>

type CalendarProps =
  | ({
      mode: 'single'
    } & SingleProps)
  | ({
      mode?: undefined
    } & SingleProps)
  | ({
      mode: 'range'
    } & RangeProps)

const DEFAULT_FROM_YEAR = 1950
const DEFAULT_TO_YEAR_OFFSET = 15

const Calendar = ({
  mode = 'single',
  weekStartsOn = 1,
  numberOfMonths = 1,
  locale,
  className,
  classNames,
  fromYear = DEFAULT_FROM_YEAR,
  toYear = new Date().getFullYear() + DEFAULT_TO_YEAR_OFFSET,
  fromDate: fromDateProp,
  toDate: toDateProp,
  captionLayout = 'dropdown-buttons',
  enableYearNavigation: _enableYearNavigation,
  ...rest
}: CalendarProps & {
  /** @deprecated year range uses dropdown; double chevrons removed */
  enableYearNavigation?: boolean
}) => {
  const fromDate = fromDateProp ?? new Date(fromYear, 0, 1)
  const toDate = toDateProp ?? new Date(toYear, 11, 31)

  return (
    <DayPicker
      mode={mode}
      weekStartsOn={weekStartsOn}
      numberOfMonths={numberOfMonths}
      locale={locale}
      showOutsideDays={numberOfMonths === 1}
      captionLayout={captionLayout}
      fromYear={fromYear}
      toYear={toYear}
      fromDate={fromDate}
      toDate={toDate}
      className={cn('p-0', className)}
      classNames={{
        root: 'rdp-root',
        vhidden: 'sr-only',
        months: 'flex flex-col gap-4 sm:flex-row sm:space-y-0 sm:space-x-4',
        month: cn(
          'space-y-3 rounded-2xl border border-neutral-200/90 bg-neutral-50/90 p-3 shadow-sm',
          'dark:border-neutral-700 dark:bg-neutral-900/60',
        ),
        caption: 'relative flex min-h-[3rem] items-center justify-center px-1 py-1',
        caption_dropdowns: cn(
          'absolute left-1/2 top-1/2 z-[1] flex -translate-x-1/2 -translate-y-1/2 items-center gap-2',
        ),
        caption_label: cn(
          'pointer-events-none flex items-center justify-center gap-1.5 text-sm font-medium text-neutral-900',
          'dark:text-neutral-100',
        ),
        dropdown_month: cn(
          'relative inline-flex min-w-[8.25rem] shrink-0 rounded-xl border border-neutral-200 bg-white py-1.5 pl-3 pr-2 shadow-sm',
          'dark:border-neutral-600 dark:bg-neutral-800',
        ),
        dropdown_year: cn(
          'relative inline-flex min-w-[5.5rem] shrink-0 rounded-xl border border-neutral-200 bg-white py-1.5 pl-3 pr-2 shadow-sm',
          'dark:border-neutral-600 dark:bg-neutral-800',
        ),
        dropdown: cn(
          'absolute inset-0 z-[2] m-0 h-full w-full cursor-pointer appearance-none border-0 p-0 opacity-0',
          'font-inherit',
        ),
        dropdown_icon: 'ml-0.5 size-3 shrink-0 text-blue-500 dark:text-blue-400',
        nav: 'relative z-0 flex w-full items-center justify-between gap-2 px-0.5',
        nav_button: cn(
          'inline-flex size-9 shrink-0 select-none items-center justify-center rounded-full border border-neutral-200/90 bg-white p-0 shadow-sm outline-none transition',
          'text-neutral-600 hover:border-blue-200 hover:bg-blue-50/80 hover:text-blue-700',
          'disabled:pointer-events-none disabled:opacity-40',
          'dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:border-blue-500 dark:hover:bg-blue-950/40',
          focusRing,
        ),
        nav_button_previous: '',
        nav_button_next: '',
        nav_icon: 'size-4 shrink-0',
        table: 'w-full border-collapse',
        head_row: 'flex',
        head_cell: 'w-10 text-center text-xs font-medium text-neutral-500 dark:text-neutral-400 sm:w-11',
        row: 'mt-1 flex w-full',
        cell: cn(
          'relative flex h-10 w-10 items-center justify-center p-0 text-center sm:h-11 sm:w-11',
          'text-neutral-900 dark:text-neutral-50',
        ),
        day: cn(
          'flex size-10 items-center justify-center rounded-lg border border-transparent bg-white text-sm font-normal transition',
          'text-neutral-900 hover:border-blue-200 hover:bg-blue-50/70 hover:text-blue-800',
          'dark:bg-neutral-800/40 dark:text-neutral-100 dark:hover:border-blue-500 dark:hover:bg-blue-950/50',
          focusRing,
        ),
        day_today: 'font-semibold text-blue-700 dark:text-blue-300',
        day_selected: cn(
          '!border-blue-600 !bg-blue-600 !text-white hover:!bg-blue-600 hover:!text-white',
          'dark:!border-blue-500 dark:!bg-blue-500',
        ),
        day_disabled: 'pointer-events-none text-neutral-300 line-through opacity-60 dark:text-neutral-600',
        day_outside: 'text-neutral-300 dark:text-neutral-600',
        day_range_middle: cn(
          '!rounded-none border-x-0 !border-blue-100 !bg-blue-50 !text-blue-800',
          'dark:!border-blue-900/50 dark:!bg-blue-950/60 dark:!text-blue-100',
        ),
        day_range_start: '!rounded-r-none rounded-l-lg',
        day_range_end: '!rounded-l-none rounded-r-lg',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        IconLeft: () => <RiArrowLeftSLine aria-hidden className="size-4" />,
        IconRight: () => <RiArrowRightSLine aria-hidden className="size-4" />,
      }}
      {...(rest as SingleProps & RangeProps)}
    />
  )
}

Calendar.displayName = 'Calendar'

export { Calendar, type Matcher }
